import {
  FieldValues,
  UseFormSetError,
  useFormContext,
  useWatch
} from "react-hook-form";
import OutlineButton from "../buttons/OutlineButton";
import { EditorSection } from "./useEditorSections";
import {
  SeverityScore,
  blankSeverityScore
} from "../forms/inputs/SeverityScoreSelector";

export type EditorAction<T extends FieldValues> = {
  type?: "button" | "submit" | "reset";
  label: string;
  disabled?: boolean;
  onClick?: (values: T, setError: UseFormSetError<T>) => void;
};

const EditorTracker = <T extends FieldValues>({
  sections,
  actions
}: {
  sections: EditorSection[];
  actions: EditorAction<T>[];
}) => {
  const {
    formState: { errors },
    getValues,
    setError
  } = useFormContext<T>();
  const values = useWatch();

  return (
    <section className="sticky top-0 h-fit">
      <article className="flex h-fit flex-col gap-2 rounded-xl px-5 py-8 shadow-pagination xl:mt-7">
        {sections.map((section, index) => (
          <div key={index} className="px-1">
            <h1
              className={
                "mb-1 text-sm font-medium " +
                (section.inputs.every(input => values[input.name])
                  ? "text-ctb-green-400"
                  : "text-ctb-blue-50")
              }
            >
              {section.title}
            </h1>

            <div className="flex flex-col gap-[1px] pl-1">
              {section.inputs.map((input, index) => {
                const error = errors[input.name] !== undefined;

                const validField =
                  values[input.name] !== undefined &&
                  values[input.name] !== null &&
                  values[input.name] !== "" &&
                  !(
                    input.name === "severity" &&
                    (values[input.name] === undefined ||
                      (values[input.name] as SeverityScore).score ===
                        blankSeverityScore.score)
                  );

                return (
                  <div key={index} className="flex items-center gap-1">
                    <div
                      className={
                        "h-6 w-2 " +
                        (error
                          ? "bg-ctb-red-500"
                          : validField
                          ? "bg-green-400"
                          : "bg-ctb-blue-50")
                      }
                    />
                    <p
                      className={
                        "text-xs " +
                        (error
                          ? "text-ctb-red-500"
                          : validField
                          ? "text-green-400"
                          : "text-ctb-blue-50")
                      }
                    >
                      {input.title}{" "}
                      {input.required ? (
                        <span
                          className={
                            error
                              ? "text-ctb-red-500"
                              : validField
                              ? "text-green-400"
                              : "text-ctb-red-500"
                          }
                        >
                          *
                        </span>
                      ) : (
                        ""
                      )}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        ))}

{actions.map((action, index) => (
  <OutlineButton
    key={index}
    type={action.type}
    className={index === 0 ? "mt-10" : ""}
    disabled={action.disabled}  // Add this line
    onClick={() => {
      if (action.onClick) {
        action.onClick(getValues(), setError);
      }
    }}
  >
    {action.label}
  </OutlineButton>
))}
      </article>
    </section>  
  );
};

export default EditorTracker;
